import { Injectable, NgZone } from "@angular/core";
import { NgRedux } from "@angular-redux/store";
import { Observable, Subject, merge } from "rxjs";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";
import * as _ from "lodash";

import { IAppState } from "../store/IAppState";
import { PlanningBlock } from "../pb-screen/models/planning-block/planning-block.interface";
import { DepotGuardEntry, DepotOptimisticGuard } from "../store/depot-optimistic-guard.models";
import { DevLoggerService } from "../shared/services/dev-logger.service";
import { SharedActions } from "../shared/shared.actions";

/**
 * DepotOptimisticGuardService
 *
 * Defensive ledger for the depot screens: keeps a client-side record of every PB the user
 * just planned/unplanned and reconciles it against the live store. Because the backend is
 * old and flaky, the goal is that a PB the user acted on can NEVER silently vanish:
 *  - the reducers refuse to drop a guarded PB (reload merge + DELETE splice),
 *  - this service detects when the server stops returning a guarded PB, FLAGS it (amber row
 *    chip + console), and fires ONE silent re-fetch to self-correct,
 *  - when the server returns the PB cleanly, the entry is confirmed and aged out,
 *  - a hard TTL guarantees the ledger self-cleans even if everything goes sideways.
 */
@Injectable({ providedIn: "root" })
export class DepotOptimisticGuardService {

  /** Hard cap — any entry older than this is force-resolved so the ledger self-cleans even if
   *  the user never dismisses the pinned row. Generous so "pin until dismiss" isn't cut short. */
  private static readonly MAX_TTL_MS = 30 * 60 * 1000;

  private readonly _refetch = new Subject<void>();
  /** Emits when a missing guarded PB warrants one silent reload of the depot grid. */
  public readonly refetchRequested$ = this._refetch.asObservable();

  private readonly guard$ = this.ngRedux.select<DepotOptimisticGuard>("depotOptimisticGuard");

  /** Live list of all guard entries — used by the depot grids to pin filter-hidden rows. */
  public readonly entries$: Observable<DepotGuardEntry[]> = this.guard$.pipe(
    map(g => Object.values(g || {})));

  constructor(
    private readonly ngRedux: NgRedux<IAppState>,
    private readonly zone: NgZone,
    private readonly devLogger: DevLoggerService,
    private readonly sharedActions: SharedActions,
  ) {
    // React quickly to store changes…
    merge(
      this.guard$,
      this.ngRedux.select<PlanningBlock[]>("depotInPlanningBlocks"),
      this.ngRedux.select<PlanningBlock[]>("depotOutPlanningBlocks"),
    ).pipe(debounceTime(60)).subscribe(() => this.reconcileNow());

    // …and tick periodically so confirm-grace age-out and the TTL reaper run while idle.
    this.zone.runOutsideAngular(() => {
      setInterval(() => this.zone.run(() => this.reconcileNow()), 1_500);
    });
  }

  /** This tab's id — same lazy sessionStorage.tabID the channel layer uses for echo suppression. */
  private tabId(): string {
    return sessionStorage.tabID || (sessionStorage.tabID = String(Math.random()));
  }

  /** Record a plan/unplan intent so its PBs are protected from silent server drops.
   *  Transmitted over the userChannel so the planner's other tab pins/badges it identically. */
  register(pbs: PlanningBlock[], action: "plan" | "unplan", opId: string): void {
    if (!pbs?.length) return;
    const now = new Date().toISOString();
    const ownerTabId = this.tabId();
    const entries: DepotGuardEntry[] = pbs.map(pb => ({
      pbId: String(pb.id),
      action,
      opId,
      snapshot: _.cloneDeep(pb),
      origin: this.originOf(pb),
      registeredAt: now,
      state: "pending",
      ownerTabId,
    }));
    this.sharedActions.registerDepotGuard(entries);
  }

  /** Record the outcome (e.g. the TO planned into) on the guard entries, for the kept badge. */
  markResult(pbIds: string[], toId?: string): void {
    if (pbIds?.length) this.sharedActions.setDepotGuardResult(pbIds.map(id => String(id)), toId);
  }

  /** Remove guard entries (confirmed, dismissed, or expired). */
  resolve(pbIds: string[]): void {
    if (pbIds?.length) this.sharedActions.resolveDepotGuard(pbIds);
  }

  /** User dismissed the discrepancy chip on a row. */
  dismiss(pbId: string | number): void {
    this.resolve([String(pbId)]);
  }

  /** Live guard entry for a single PB row (drives the amber discrepancy chip). */
  guardFor(pbId: string | number): Observable<DepotGuardEntry | null> {
    const id = String(pbId);
    return this.guard$.pipe(map(g => (g && g[id]) || null), distinctUntilChanged());
  }

  // ── internals ──

  private originOf(pb: PlanningBlock): "depotIn" | "depotOut" | "both" {
    switch ((pb as any).containerPlanningType) {
      case 1: return "depotOut";
      case 2: return "depotIn";
      default: return "both"; // type 4 (in+out) or unknown — protect on both screens
    }
  }

  private reconcileNow(): void {
    const state = this.ngRedux.getState();
    const guard = state?.depotOptimisticGuard;
    if (!guard) return;
    const entries = Object.values(guard);
    if (!entries.length) return;

    const masterIn = new Set((state.depotInPlanningBlocks || []).map(pb => String(pb.id)));
    const masterOut = new Set((state.depotOutPlanningBlocks || []).map(pb => String(pb.id)));
    const now = Date.now();
    const toResolve: string[] = [];

    for (const entry of entries) {
      const ageMs = now - new Date(entry.registeredAt).getTime();

      // Backstop: never let an entry linger forever (e.g. user never dismissed).
      if (ageMs >= DepotOptimisticGuardService.MAX_TTL_MS) {
        this.devLogger.warn("DEPOT-GUARD", "Force-resolving stale guard entry:", entry.pbId);
        toResolve.push(entry.pbId);
        continue;
      }

      const inMaster = this.isPresent(entry, masterIn, masterOut);

      if (inMaster) {
        // KEEP the entry — pin until the user dismisses (their chosen behaviour). Never auto-
        // resolve on transient visibility: the plan chain (prePlan → group → send) takes several
        // seconds and a final step can still drop the row, so the guard must stay active until
        // the user acknowledges the result badge (or the TTL backstop above). If the quick filter
        // hides the row, the grid pins it; if it's visible, the entry just carries the badge.
        continue;
      }
      // Gone from the master list. ONLY the owning tab runs discrepancy detection: the other tab
      // may not even have this screen's master loaded (it shows the opposite depot screen), so it
      // would false-positive. The owner's flag mutation broadcasts, so the amber chip still syncs
      // to both tabs. Guard state is shared across tabs via the userChannel.
      const isOwner = !entry.ownerTabId || entry.ownerTabId === this.tabId();
      if (!isOwner) continue;
      if (entry.state !== "flagged" || !entry.refetchedAt) {
        const firstRefetch = !entry.refetchedAt;
        this.devLogger.warn(
          "DEPOT-GUARD",
          `PB ${entry.pbId} missing from server return after ${entry.action} — pinning row.`,
          "| op:", entry.opId, "| origin:", entry.origin, firstRefetch ? "| firing 1 silent re-fetch" : "",
        );
        this.sharedActions.flagDepotGuard(
          entry.pbId,
          `Server stopped returning this planning block after ${entry.action}.`,
          firstRefetch,
        );
        if (firstRefetch) this._refetch.next();
      }
    }

    if (toResolve.length) this.resolve(toResolve);
  }

  private isPresent(entry: DepotGuardEntry, inIds: Set<string>, outIds: Set<string>): boolean {
    if (entry.origin === "depotIn") return inIds.has(entry.pbId);
    if (entry.origin === "depotOut") return outIds.has(entry.pbId);
    return inIds.has(entry.pbId) || outIds.has(entry.pbId);
  }
}
